// src/components/Breadcrumbs.tsx
"use client";

import classNames from "classnames";
import { Home } from "lucide-react";
import React from "react";

import { useTranslations } from "@/context/TranslationsContext";
import { BreadcrumbItem } from "@/types/types";

import LocalizedLink from "./LocalizedLink";

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

const slugify = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "_");

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const { t, direction } = useTranslations();
  const isRTL = direction === "rtl";

  const filtered = items.filter((i) => i.name !== "Countries");

  return (
    <nav
      className="container mx-auto p-4 text-base text-gray-600"
      aria-label={t("breadcrumbs.ariaLabel")}
    >
      <ol
        className={classNames("list-none p-0 inline-flex items-center", {
          "justify-start": !isRTL,
          "justify-end": isRTL,
        })}
      >
        {filtered.map((item, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === filtered.length - 1;

          const label = isFirst
            ? t("breadcrumbs.home")
            : t(`regions.${slugify(item.name)}`, {}, item.name);

          return (
            <li className="flex items-center" key={idx}>
              {item.href && !isLast ? (
                <LocalizedLink
                  href={item.href}
                  className="text-blue-600 hover:underline flex items-center gap-3"
                >
                  {isFirst && (
                    <Home
                      className="w-4 h-4 text-gray-500"
                      aria-hidden="true"
                    />
                  )}
                  {label}
                </LocalizedLink>
              ) : (
                <span className="text-gray-500 flex items-center gap-3">
                  {isFirst && (
                    <Home
                      className="w-4 h-4 text-gray-500"
                      aria-hidden="true"
                    />
                  )}
                  {label}
                </span>
              )}

              {!isLast && (
                <svg
                  className={classNames("w-4 h-4 text-gray-400 mx-2", {
                    "transform rotate-180": isRTL,
                  })}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
